// @flow
import * as React from 'react'
import tw from 'tailwind-styled-components'
import Button from 'ui/atoms/Button'
import Grid from 'ui/atoms/Grid'
import FormContainer from 'ui/form/FormContainer'
import Icons from 'ui/Icons'
import DateRangeField from 'ui/molecules/DateRangeField'
import SelectField from 'ui/molecules/SelectField'
import TextField from 'ui/molecules/TextField'
import { ZentakeContext } from 'utils/ZentakeContext'

import useAuditLog from '../hooks'
import type { AuditLogFilterProps } from '../types'

const FilterRow = tw.div`
  my-4
`

function AuditLogsFilter(): React.Node {
  const [search, setSearch] = React.useState<string | void>(undefined)
  const [filters, setFilters] = React.useState<AuditLogFilterProps>({
    form: undefined,
    packet: undefined,
    response: undefined,
    packet_response: undefined,
    status: undefined,
    patient_status: undefined,
    is_file_upload: undefined,
    created_at__gte: undefined,
    created_at__lte: undefined,
    document: undefined,
  })
  const [showFilters, setShowFilters] = React.useState(false)
  const { makeFilter, makeSearch } = useAuditLog()
  const {
    updateForms,
    updatePackets,
    updatePdfDocuments,
    forms,
    packets,
    pdfDocuments,
  } = React.useContext(ZentakeContext)

  const formOptions = React.useMemo(() => {
    const all = [{ value: '', label: 'All Forms' }]
    return all.concat(
      forms.map((form) => ({ value: form.id, label: form.name }))
    )
  }, [forms])
  const packetOptions = React.useMemo(() => {
    const all = [{ value: '', label: 'All Packets' }]
    return all.concat(
      packets.map((packet) => ({ value: packet.id, label: packet.name }))
    )
  }, [packets])

  const pdfDocumentOptions = React.useMemo(() => {
    const all = [{ value: '', label: 'All Documents' }]
    return all.concat(
      pdfDocuments.map((pdfDocument) => ({
        value: pdfDocument.id,
        label: pdfDocument.name,
      }))
    )
  }, [pdfDocuments])

  React.useEffect(() => {
    updateForms()
    updatePackets()
    updatePdfDocuments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      makeSearch(search)
    }, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [makeSearch, search])

  React.useEffect(() => {
    makeFilter(filters)
  }, [
    makeFilter,
    filters.form,
    filters.packet,
    filters.status,
    filters.patient_status,
    filters.is_file_upload,
    filters.created_at__gte,
    filters.created_at__lte,
    filters.document,
    filters,
  ])

  return (
    <FilterRow>
      <Button
        $primary
        icon={<Icons.FilterAlt />}
        iconPosition="right"
        onClick={() => setShowFilters((previous) => !previous)}
      >
        Filter
      </Button>
      <div className={showFilters ? 'block' : 'hidden'}>
        <FormContainer className="bg-gray-200 border-gray-300 pb-2 px-2 mt-1">
          <Grid cols={4} gap="small">
            <TextField
              smallLabel
              useEvent
              label="Client Name"
              onChange={(e) => setSearch(e.target.value || undefined)}
            />
            <SelectField
              smallLabel
              label="Form"
              onChange={(e) =>
                setFilters({ ...filters, form: e.target.value || undefined })
              }
              options={formOptions}
            />
            <SelectField
              smallLabel
              label="Packet"
              onChange={(e) =>
                setFilters({ ...filters, packet: e.target.value || undefined })
              }
              options={packetOptions}
            />
            <SelectField
              smallLabel
              label="Document"
              onChange={(e) =>
                setFilters({
                  ...filters,
                  document: e.target.value || undefined,
                })
              }
              options={pdfDocumentOptions}
            />
          </Grid>
          <Grid cols={4} gap="small">
            <SelectField
              smallLabel
              label="Status"
              onChange={(e) =>
                setFilters({
                  ...filters,
                  status: e.target.value || undefined,
                })
              }
              options={[
                { value: '', label: 'All' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'succeed', label: 'Succeed' },
                { value: 'failed', label: 'Failed' },
                { value: 'restarted', label: 'Restarted' },
                { value: 'warning', label: 'Warning' },
                { value: 'missing', label: 'Missing' },
              ]}
            />
            <SelectField
              smallLabel
              label="Patient Status"
              onChange={(e) =>
                setFilters({
                  ...filters,
                  patient_status: e.target.value || undefined,
                })
              }
              options={[
                { value: '', label: 'All' },
                { value: 'none', label: 'None' },
                { value: 'created', label: 'Created' },
                { value: 'updated', label: 'Updated' },
                {
                  value: 'provider_not_connected',
                  label: 'Provider Not Connected',
                },
              ]}
            />
            <SelectField
              smallLabel
              label="File Upload"
              onChange={(e) =>
                setFilters({
                  ...filters,
                  is_file_upload:
                    e.target.value === ''
                      ? undefined
                      : e.target.value === 'Yes'
                      ? true
                      : false,
                })
              }
              options={[
                { value: '', label: 'Yes/No' },
                { value: 'Yes', label: 'Yes' },
                { value: 'No', label: 'No' },
              ]}
            />
            <DateRangeField
              smallLabel
              utcValue
              label="Sent At"
              onChange={(value) => {
                if (value !== null) {
                  setFilters({
                    ...filters,
                    created_at__gte: value[0][0] || undefined,
                    created_at__lte: value[0][1] || undefined,
                  })
                } else {
                  setFilters({
                    ...filters,
                    created_at__gte: undefined,
                    created_at__lte: undefined,
                  })
                }
              }}
              value={
                filters.created_at__gte || filters.created_at__lte
                  ? [
                      filters.created_at__gte || null,
                      filters.created_at__lte || null,
                    ]
                  : undefined
              }
            />
          </Grid>
        </FormContainer>
      </div>
    </FilterRow>
  )
}

export default AuditLogsFilter
